<template>
	<div @keydown.esc="closeAllModals" class="group-settings-options">
		<FailBanner v-if="showFail" @close="showFail = false" marginright="50px">
			<template #alerttext>{{ alertText }}</template>
		</FailBanner>

		<div
			class="super-group-wrapper"
			v-if="chat.is_supergroup"
			@click="showSuperGroupModal = true"
		>
			<RocketLaunchIcon halo type="regular" class="super-group-icon" />
			<div class="super-group-content">
				<div class="super-group-title" v-text="$t('chat_super_title')" />
				<div class="super-group-desc">
					{{ $t('groupsettings_super_group_description') }}
					<div class="super-group-link" v-text="$t('common_learn_more')" />
				</div>
			</div>
		</div>

		<div class="group-name-wrapper" v-if="!chat.isdm">
			<p class="group-settings-sub-heading" style="margin-top: 5px;">
				<span>{{
					$t('groupsettings_name_settings_groupname').toLowerCase()
				}}</span>
			</p>
			<div class="group-settings-group-name">
				<input
					@focus="focusGroupName"
					@keyup="focusGroupName"
					id="group-name"
					maxlength="100"
					class="group-settings-name"
					:disabled="!canChangeTitle"
					type="text"
					name="txtGroupName"
					v-model="title"
					@keyup.enter="updateGroupName"
				/>
				<div
					v-if="renameIndicator"
					class="rename-group-name"
					@click="updateGroupName"
				>
					{{ $t('chat_messageaction_save') }}
				</div>
			</div>

			<p class="group-settings-sub-heading" style="margin-top: 20px;">
				<span>{{
					$t('groupsettings_name_settings_nickname').toLowerCase()
				}}</span>
			</p>
			<div class="group-settings-group-nickname" style="position: relative;">
				<input
					:placeholder="$t('groupsettings_name_settings_nickname_placeholder')"
					maxlength="100"
					class="group-settings-nickname nickname"
					type="text"
					name="txtNickname"
					v-model="groupNickname"
					@keyup.enter="updateGroupNickname"
					data-cy="nickname"
				/>
				<div
					v-if="groupNicknameIndicator"
					class="rename-nickname"
					@click="updateGroupNickname"
				>
					{{ $t('chat_messageaction_save') }}
				</div>
			</div>
			<div class="nickname-info">
				{{ $t('groupsettings_name_settings_nickname_description') }}
			</div>
		</div>

		<div class="group-category-wrapper" v-if="!chat.isdm">
			<p class="group-settings-sub-heading">
				<span>{{
					$t('groupsettings_option_category_title').toLowerCase()
				}}</span>
			</p>
			<div
				class="group-settings-category group-settings-select-class select-no-clear"
			>
				<Dropdown
					v-model="category_id"
					:options="categoryOptions"
					:disabled="!canChangeCategory"
					:loading="savingCategory"
					class="group-category-select"
				/>
			</div>

			<p class="group-settings-sub-heading" style="margin-top: 20px;">
				<span>{{
					$t('groupsettings_name_settings_nickname').toLowerCase()
				}}</span>
			</p>
			<div class="group-settings-category-nickname" style="position: relative;">
				<input
					:placeholder="$t('groupsettings_name_settings_nickname_placeholder')"
					maxlength="100"
					class="group-settings-nickname nickname"
					type="text"
					name="txtNickname"
					v-model="categoryNickname"
					@keyup.enter="updateCategoryNickname()"
					data-cy="categoryNickname"
				/>
				<div
					v-if="categoryNicknameIndicator"
					class="rename-nickname"
					@click="updateCategoryNickname()"
				>
					{{ $t('chat_messageaction_save') }}
				</div>
			</div>
			<div class="nickname-info">
				{{ $t('groupsettings_name_settings_nickname_description') }}
			</div>
		</div>

		<div class="change-controls" v-if="groupOwner && !chat.isdm">
			<GroupPermissions :chat="chat" />
		</div>

		<ButtonDescription
			data-cy="hideGroupBtn"
			:buttonText="$t('groupsettings_hide_confirm_button')"
			@buttonClick="determineHide()"
		>
			{{ hideBtnExplanation }}
		</ButtonDescription>

		<ButtonDescription
			data-cy="leaveGroupBtn"
			v-if="!chat.isdm && canLeaveGroup"
			:buttonText="$t('groupsettings_option_leavebubble')"
			@buttonClick="showLeaveModal = true"
		>
			{{ $t('groupsettings_option_leavebubble_desc') }}
		</ButtonDescription>

		<ButtonDescription
			data-cy="deleteGroupBtn"
			v-if="!chat.isdm && canDeleteChat"
			@buttonClick="showDeleteModal = true"
			color="red"
			:buttonText="$t('groupsettings_option_deletebubble')"
		>
			{{ $t('groupsettings_option_deletebubble_desc') }}
		</ButtonDescription>

		<div v-if="showInputSpinner" class="group-settings-input-spinner">
			<div id="verySmallSpinner" class="ios-spinner">
				<div class="bar1"></div>
				<div class="bar2"></div>
				<div class="bar3"></div>
				<div class="bar4"></div>
				<div class="bar5"></div>
				<div class="bar6"></div>
				<div class="bar7"></div>
				<div class="bar8"></div>
				<div class="bar9"></div>
				<div class="bar10"></div>
				<div class="bar11"></div>
				<div class="bar12"></div>
			</div>
		</div>

		<LeaveModal
			v-if="showLeaveModal"
			@close="showLeaveModal = false"
			:chatId="bubble_id"
			data-cy="leaveModal"
		/>

		<HideModal
			v-if="showHideModal"
			@close="showHideModal = false"
			@hide="hideGroup()"
			:buttonText="'Hide Group'"
			:showConfirm="true"
			:showSpinner="showHideSpinner"
			data-cy="hideModal"
		>
			<template #titletext>{{ $t('chat_drawer_section_hide_group') }}</template>
			<template #subtext>{{
				$t('chat_drawer_section_hide_description_group')
			}}</template>
		</HideModal>

		<DeleteModal
			v-if="showDeleteModal"
			@close="showDeleteModal = false"
			@delete="deleteGroup"
			:title="$t('groupsettings_option_deletebubble')"
			:buttonText="'Delete Group'"
			:showConfirm="true"
			:isProcessing="showDeleteSpinner"
			:bodyText="$t('groupsettings_delete_confirm_message')"
			data-cy="deleteModal"
		/>

		<SuperGroupModal
			v-if="showSuperGroupModal"
			:chatId="chat.id"
			@close="showSuperGroupModal = false"
		/>
	</div>
</template>

<script>
import store, {
	getMyMembership,
	setMembership,
	getChat,
	setChat,
	removeChatFromCategory,
	getCategory,
	getCategories,
	getMyMembershipForBubble,
} from '@/store'
import { post } from '@/helpers/api'

import { ButtonDescription, Dropdown } from '@/components/common'
import FailBanner from '@/components/FailBanner'
import DeleteModal from '@/components/DeleteModal'
import HideModal from '@/components/HideModal'
import LeaveModal from '@/components/Home/LeaveModal'
import { redirectToLastActiveGroup } from '@/helperMethods'
import GroupPermissions from './GroupPermissions'
import SuperGroupModal from '@/components/Home/SuperGroupModal'
import { RocketLaunchIcon } from '@/icons'
import {
	chatCan,
	CHANGE_TITLE,
	CHANGE_CATEGORY,
	LEAVE_GROUP,
	DELETE_GROUP,
} from '@/helpers/permissions'

export default {
	components: {
		Dropdown,
		FailBanner,
		DeleteModal,
		LeaveModal,
		HideModal,
		ButtonDescription,
		GroupPermissions,
		RocketLaunchIcon,
		SuperGroupModal,
	},
	props: ['bubble_id'],
	data() {
		return {
			store,
			options: [],
			chat: {},
			title: '',
			categoryNickname: '',
			originalCategoryNickname: '',
			groupNickname: '',
			originalGroupNickname: '',
			categoryNicknameIndicator: false,
			groupNicknameIndicator: false,
			originalTitle: '',
			category_title: 'None',
			category_id: null,
			showDeleteModal: false,
			showLeaveModal: false,
			showSpinner: false,
			showInputSpinner: false,
			renameIndicator: false,
			categoryOptions: [],
			categorySelected: null,
			showPermissions: false,
			permissionOpts: ['Owners', 'Everyone'],
			changetitle: null,
			changecategory: null,
			addmember: null,
			removemember: null,
			leavegroup: null,
			deletegroup: null,
			videosessionrecordcloud: null,
			createannouncement: null,
			showFail: false,
			alertText: '',
			showDeleteSpinner: false,
			showHideSpinner: false,
			showHideModal: false,
			showSuperGroupModal: false,
			savingCategory: false,
		}
	},

	emits: ['peopleClick'],

	watch: {
		categoryNickname: function (val) {
			if (val === this.originalCategoryNickname) {
				this.categoryNicknameIndicator = false
			} else {
				this.categoryNicknameIndicator = true
			}
		},

		groupNickname: function (val) {
			if (val === this.originalGroupNickname) {
				this.groupNicknameIndicator = false
			} else {
				this.groupNicknameIndicator = true
			}
		},

		showPermissions: function (val, oldVal) {
			var permissionsBox = document.querySelector('.permissions')
			if (val) {
				setTimeout(function () {
					permissionsBox.style.overflow = 'unset'
				}, 200)
			} else {
				permissionsBox.style.overflow = 'hidden'
			}
		},

		categorySelected: function (val, oldVal) {
			if (!oldVal) return
			if (val?.id !== oldVal?.id) {
				this.updateCategory(val.id)
			}
		},

		changetitle: function (val, oldVal) {
			if (val !== oldVal) {
				this.updatePermission(
					'changetitle',
					this.changetitle,
					'.change-title-permission'
				)
			} else {
				return
			}
		},

		createannouncement: function (val, oldVal) {
			if (val !== oldVal) {
				this.updatePermission(
					'create_announcement',
					this.createannouncement,
					'.create-announcement-permission'
				)
			} else {
				return
			}
		},

		changecategory: function (val, oldVal) {
			if (val !== oldVal) {
				this.updatePermission(
					'changecategory',
					this.changecategory,
					'.change-category-permission'
				)
			} else {
				return
			}
		},

		addmember: function (val, oldVal) {
			if (val !== oldVal) {
				this.updatePermission(
					'addmember',
					this.addmember,
					'.add-members-permission'
				)
			} else {
				return
			}
		},

		removemember: function (val, oldVal) {
			if (val !== oldVal) {
				this.updatePermission(
					'removemember',
					this.removemember,
					'.remove-members-permission'
				)
			} else {
				return
			}
		},

		leavegroup: function (val, oldVal) {
			if (val !== oldVal) {
				this.updatePermission(
					'leavegroup',
					this.leavegroup,
					'.leave-group-permission'
				)
			} else {
				return
			}
		},

		videosessionrecordcloud: function (val, oldVal) {
			if (val !== oldVal) {
				this.updatePermission(
					'videosessionrecordcloud',
					this.videosessionrecordcloud,
					'.record-livestreams-permission'
				)
			} else {
				return
			}
		},

		chat(chat) {
			this.title = chat.title
			this.originalTitle = this.title
		},

		category_id(id) {
			const category = getCategory(id)
			if (category.usercategory?.alias) {
				this.categoryNickname = category.usercategory.alias
			} else {
				this.categoryNickname = ''
			}

			this.updateCategory(id)
			setTimeout(() => {
				this.categoryNicknameIndicator = false
			}, 100)
		},
	},

	created() {
		this.chat = getChat(this.bubble_id)
		this.title = this.chat.title
		this.originalTitle = this.title
		this.category_id = this.chat.category_id
	},

	mounted() {
		this.getCategories()

		this.categoryNickname =
			getCategory(this.category_id)?.usercategory?.alias ?? ''
		this.originalCategoryNickname = this.categoryNickname

		this.groupNickname = getMyMembershipForBubble(this.bubble_id)?.alias ?? ''
		this.originalGroupNickname = this.groupNickname
	},

	computed: {
		groupIsHidden() {
			const membership = getMyMembership()
			return membership?.ishidden
		},

		titlePlaceholder() {
			if (!this.chat) return ''
			return this.permissionPlaceholder(this.chat.changetitle)
		},

		groupOwner() {
			return getMyMembership()?.role === 'owner'
		},

		displayname() {
			return this.chat.displayname
		},

		canChangeTitle() {
			return chatCan(this.chat, CHANGE_TITLE)
		},

		canChangeCategory() {
			return chatCan(this.chat, CHANGE_CATEGORY)
		},

		canLeaveGroup() {
			return chatCan(this.chat, LEAVE_GROUP)
		},

		canDeleteChat() {
			return chatCan(this.chat, DELETE_GROUP)
		},

		hideBtnExplanation() {
			if (this.chat.is_dm && this.chat.dm_memberships_count > 2) {
				return this.$t('groupsettings_hidemultidm_explanation')
			} else if (this.chat.is_dm) {
				return this.$t('groupsettings_hidedm_explanation')
			} else {
				return this.$t('groupsettings_hidegroup_explanation')
			}
		},
	},

	methods: {
		showPeople() {
			this.$emit('peopleClick')
		},

		determineHide() {
			if (this.groupIsHidden) {
				this.unhideGroup()
			} else {
				this.showHideModal = true
			}
		},

		unhideGroup() {
			const membership = getMyMembership()
			membership.ishidden = false
			setMembership(membership)

			let post_data = {
				bubble_id: this.bubble_id,
				ishidden: 0,
			}

			post('/api/v1/membership.update', post_data)
				.then(() => {})
				.catch((e) => {
					membership.ishidden = true
					setMembership(membership)
				})
		},

		changeGroupNameState() {
			if (this.title !== this.originalTitle) {
				this.renameIndicator = true
				return true
			} else {
				this.renameIndicator = false
				return false
			}
		},

		updateGroupName() {
			if (this.title === '') {
				return
			}

			this.showInputSpinner = true
			this.$nextTick(() => {
				this.placeSpinner('.group-settings-group-name')
			})

			var data = {
				bubble_id: this.bubble_id,
				title: this.title,
			}

			post('/api/v1/bubble.update', data)
				.then(({ data }) => {
					setChat(data.bubble)
					this.showInputSpinner = false
					this.renameIndicator = false
					this.originalTitle = data.bubble.title
					this.$alert.success(
						this.$t('admin_people_changes_saved_confirmation_label')
					)
				})
				.catch((error) => {
					console.error(error)
					this.showInputSpinner = false
					this.renameIndicator = false
					this.$alert.error()
				})
		},

		updateCategory(category_id) {
			const chat = getChat(this.bubble_id)
			if (chat.category_id === category_id) return

			this.savingCategory = true

			this.$nextTick(() => {
				this.placeSpinner('.group-settings-category')
			})

			var data = {
				bubble_id: this.bubble_id,
				category_id: category_id,
			}

			post('/api/v1/bubble.update', data)
				.then(({ data }) => {
					removeChatFromCategory(this.bubble_id)
					setChat(data.bubble)
					this.savingCategory = false
					this.$alert.success(
						this.$t('admin_people_changes_saved_confirmation_label')
					)
				})
				.catch((error) => {
					console.error(error)
					this.savingCategory = false
					this.renameIndicator = false
					this.$alert.error()
				})
		},

		focusGroupName($event) {
			if (this.changeGroupNameState()) {
				this.$nextTick(() => {
					let rect = $event.target.parentNode.getBoundingClientRect()
					var element = document.querySelector('.rename-group-name')
					element.style.left = rect.right - 360 + 'px'
					element.style.top = rect.top + 3 + 'px'
				})
			}
		},

		updateCategoryNickname() {
			this.showInputSpinner = true
			this.$nextTick(() => {
				this.placeSpinner('.group-settings-category-nickname')
			})

			const post_data = {
				category_id: this.category_id,
				alias: this.categoryNickname,
			}

			post('/api/v1/category.update', post_data)
				.then(() => {
					this.categoryNicknameIndicator = false
					this.showInputSpinner = false

					this.originalCategoryNickname = this.categoryNickname
					this.$alert.success(
						this.$t('admin_people_changes_saved_confirmation_label')
					)
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(this.$t('common_api_error_save'))
				})
		},

		updateGroupNickname() {
			this.showInputSpinner = true
			this.$nextTick(() => {
				this.placeSpinner('.group-settings-group-nickname')
			})

			const post_data = {
				bubble_id: this.bubble_id,
				alias: this.groupNickname,
			}

			post('/api/v1/membership.update', post_data)
				.then(({ data }) => {
					this.groupNicknameIndicator = false
					this.showInputSpinner = false

					setMembership(data.membership)

					this.originalGroupNickname = this.groupNickname
					this.$alert.success(
						this.$t('admin_people_changes_saved_confirmation_label')
					)
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(this.$t('common_api_error_save'))
				})
		},

		placeSpinner(selector) {
			let rect = document.querySelector(selector).getBoundingClientRect()
			let spinner = document.querySelector('.group-settings-input-spinner')

			spinner.style.top = rect.top - 42 + 'px'
			spinner.style.left = rect.right - 252 + 'px'
		},

		getCategories() {
			this.categoryOptions = getCategories().map((item) => {
				return { id: item.id, label: item.title }
			})
		},

		deleteGroup() {
			this.showDeleteSpinner = true
			post('/api/v1/bubble.delete', { bubble_id: this.bubble_id })
				.then(() => {
					this.showDeleteModal = false
					redirectToLastActiveGroup()
				})
				.catch((error) => {
					console.error(error)
					this.showSpinner = false
					this.showDeleteSpinner = false
					this.$alert.error()
				})
		},

		hideGroup() {
			// optimistically update our membership to be marked as hidden.
			const membership = getMyMembership()
			membership.ishidden = true
			setMembership(membership)

			this.showHideSpinner = true

			let post_data = {
				bubble_id: this.bubble_id,
				ishidden: 1,
			}

			post('/api/v1/membership.update', post_data)
				.then(({ data }) => {
					this.showHideModal = false
					setMembership(data.membership)
					redirectToLastActiveGroup()
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(
						this.$t('chat_drawer_section_error_hide', {
							GroupName: this.chat.title,
						})
					)

					membership.ishidden = false
					setMembership(membership)
				})
		},

		closeAllModals() {
			this.showDeleteModal = false
			this.showLeaveModal = false
		},

		permissionPlaceholder(perm) {
			if (perm === 'owner') {
				return this.$t('groupsettings_permissions_owners')
			} else if (perm === 'member') {
				return this.$t('groupsettings_permissions_everyone')
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.group-settings-sub-headings span {
	background: #fff;
	padding: 0 20px;
	text-align: left;
}

.group-settings-sub-heading {
	margin-top: 0;
	margin-bottom: 5px;
}

.group-settings-sub-heading span {
	text-transform: capitalize;
}

.group-settings-name,
.group-settings-nickname {
	height: 32px;
	width: 100%;
	border-radius: 4px;
	border: solid 1px #dfe1e3;
	padding: 16px 112px 16px 10px;
	font-family: Avenir-Medium;
	font-size: 16px;
	color: #2c2d30;
}

.group-settings-name-restricted {
	height: 55px;
	width: 100%;
	padding: 0;
	margin-bottom: 10px;
	font-family: Avenir-Roman;
	font-size: 24px;
	color: #2c2d30;
}

#group-name,
.nickname {
	outline: none;
}

.group-settings-help-icon {
	width: 20px;
	height: 20px;
	margin-top: 8px;
	margin-left: 4px;
	cursor: pointer;
}

.group-settings-help-icon:hover {
	content: url('/images/common/question-mark-green.png');
}

.change-controls {
	position: relative;
}

.group-settings-control-buttons p {
	margin: 0;
	color: #888;
	flex: 1;
}

.features h4 {
	letter-spacing: 1.25px;
}

.control-group {
	position: relative;
}

.control-group > * {
	display: inline-block;
}

.read-receipts,
.post-message {
	margin-bottom: 20px;
}

.group-label p {
	display: inline-block;
	font-family: Avenir-Roman;
	font-size: 14px;
	color: #4b4b4d;
	padding: 0;
}

.are-you-sure {
	width: 396px;
	height: 55px;
	margin: 10px auto 30px;
	font-family: Avenir-Roman;
	font-size: 20px;
	text-align: center;
	color: #4f5053;
}

.rename-group-name,
.rename-nickname {
	width: 75px;
	height: 26.9px;
	border-radius: 4px;
	border: 1px solid var(--teal-300);
	font-family: Avenir-Medium;
	font-size: 14px;
	font-weight: 500;
	text-align: center;
	padding-top: 4px;
	color: var(--teal-300);
	position: absolute;
	cursor: pointer;
}

.rename-group-name {
	top: 232px !important;
}

.rename-nickname {
	right: 5px;
	top: 4px;
}

.delete-modal-text {
	margin: 0 auto;
}

.disabled-change {
	pointer-events: none;
	border: none;
	padding-left: 0;
}

.group-settings-spinner {
	position: absolute;
	right: 12px;
	bottom: 22px;
}

.group-settings-input-spinner {
	position: absolute;
}

.group-spinner-text {
	font-family: Avenir-Roman;
	font-size: 14px;
	color: #9c9c9d;
	position: absolute;
	right: 25px;
	bottom: 0;
}

#verySmallSpinner {
	position: relative;
	width: 22px;
	height: 22px;
	display: inline-block;
	padding: 10px;
	border-radius: 10px;
	background: #ffffff;
	vertical-align: top;
	margin-top: 4.1px;
}

.ios-spinner div {
	width: 6%;
	height: 16%;
	background: #9c9c9d;
	position: absolute;
	left: 49%;
	top: 43%;
	opacity: 0;
	-webkit-border-radius: 50px;
	-webkit-animation: fade 1s linear infinite;
}

@-webkit-keyframes fade {
	from {
		opacity: 1;
	}
	to {
		opacity: 0.25;
	}
}

.ios-spinner div.bar1 {
	-webkit-transform: rotate(0deg) translate(0, -130%);
	-webkit-animation-delay: 0s;
}

.ios-spinner div.bar2 {
	-webkit-transform: rotate(30deg) translate(0, -130%);
	-webkit-animation-delay: -0.9167s;
}

.ios-spinner div.bar3 {
	-webkit-transform: rotate(60deg) translate(0, -130%);
	-webkit-animation-delay: -0.833s;
}
.ios-spinner div.bar4 {
	-webkit-transform: rotate(90deg) translate(0, -130%);
	-webkit-animation-delay: -0.7497s;
}
.ios-spinner div.bar5 {
	-webkit-transform: rotate(120deg) translate(0, -130%);
	-webkit-animation-delay: -0.667s;
}
.ios-spinner div.bar6 {
	-webkit-transform: rotate(150deg) translate(0, -130%);
	-webkit-animation-delay: -0.5837s;
}
.ios-spinner div.bar7 {
	-webkit-transform: rotate(180deg) translate(0, -130%);
	-webkit-animation-delay: -0.5s;
}
.ios-spinner div.bar8 {
	-webkit-transform: rotate(210deg) translate(0, -130%);
	-webkit-animation-delay: -0.4167s;
}
.ios-spinner div.bar9 {
	-webkit-transform: rotate(240deg) translate(0, -130%);
	-webkit-animation-delay: -0.333s;
}
.ios-spinner div.bar10 {
	-webkit-transform: rotate(270deg) translate(0, -130%);
	-webkit-animation-delay: -0.2497s;
}
.ios-spinner div.bar11 {
	-webkit-transform: rotate(300deg) translate(0, -130%);
	-webkit-animation-delay: -0.167s;
}
.ios-spinner div.bar12 {
	-webkit-transform: rotate(330deg) translate(0, -130%);
	-webkit-animation-delay: -0.0833s;
}

.group-options-category-title {
	font-family: Avenir-Roman;
	font-size: 24px;
	color: #2c2d30;
	padding-top: 10px;
}

.group-label {
	margin-left: 16px;
	position: relative;
	@include flex;
}

.permissions h4,
.permission-toggle {
	display: inline-block;
}

.permissions {
	overflow: hidden;
	border: 1px solid #dfe1e3;
	border-radius: 4px;
	padding: 10px 0;
	position: relative;
	min-height: 80px;
	max-height: 80px;
	-webkit-transition: all 0.3s ease-in-out;
	-moz-transition: all 0.3s ease-in-out;
	-ms-transition: all 0.3s ease-in-out;
	-o-transition: all 0.3s ease-in-out;
	transition: all 0.3s ease-in-out;
}

.permissions-expanded {
	max-height: 290px !important;
}

.permission-toggle {
	position: absolute;
	right: 14px;
	top: 12px;
	width: 164px;
	height: 54px;
	border-radius: 5px;
	background-color: #f2f4f5;
	padding: 16px 55px;
	cursor: pointer;
	color: #4f5053;
	z-index: 1;
}

.permissions-open {
	padding: 16px 65px;
}

.group-options-permissions {
	display: grid;
	margin-top: 80px;
	grid-gap: 0;
	grid-template-areas: 'a a';
	grid-auto-columns: 374px;
}

.collapsed-wrapper {
	width: 140px;
}

.collapsed-wrapper h4 {
	position: absolute;
	left: 15px;
	top: -5px;
	font-family: Avenir-Heavy;
	font-size: 16px;
	font-weight: 500;
	letter-spacing: -0.1px;
	color: #4f5053;
}

.collapsed-wrapper .permissions-description {
	position: absolute;
	left: 15px;
	top: 40px;
	font-family: Avenir-Roman;
	font-size: 14px;
	font-weight: normal;
	font-style: normal;
	font-stretch: normal;
	line-height: normal;
	letter-spacing: normal;
	color: #9c9c9d;
}

.permissions-tip .link-to-people {
	color: var(--teal-300);
	text-decoration: none;
}

.explanation-text {
	font-family: Avenir-Roman;
	font-size: 14px;
	color: #9c9c9d;
	align-self: center;
}

.group-settings-options {
	@include flex-column(24px);
}

.super-group-wrapper,
.group-name-wrapper,
.group-category-wrapper {
	border: 1px solid var(--neutral-200);
	border-radius: 4px;
	padding: 16px 24px 16px 16px;
	background: var(--neutral-100);
}
.super-group-wrapper {
	cursor: pointer;
	padding: 16px;
	@include flex(12px);
	.super-group-content {
		@include flex-column(4px);
		.super-group-title {
			@include fontFamily(900);
		}
		.super-group-desc {
			@include flex(8px);
			color: var(--neutral-600);
			.super-group-link {
				color: var(--teal-300);
			}
		}
	}
	> .pronto-icon {
		color: var(--teal-300);
		font-size: 18px;
		&::before {
			width: 2em;
			height: 2em;
			background-color: white;
			opacity: 1;
		}
	}
}

.nickname-info {
	font-family: Avenir-Medium;
	font-size: 14px;
	color: #8e8e8e;
	margin-top: 10px;
}

.block {
	display: block;
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.group-settings-name,
	.group-settings-nickname {
		background: var(--neutral-200) !important;
		border: solid 1px var(--neutral-200) !important;
		color: var(--neutral-800) !important;
	}
	.group-settings-name-restricted {
		color: var(--neutral-800) !important;
	}
}
</style>
